// ============================================================================
//  DashboardPage.tsx — NexusCorp ERP · Dashboard 2026
//  Modern cards · Glassmorphism · Smooth animations · Recharts
// ============================================================================
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import {
  Users, TrendingUp, FolderKanban, Ticket, Wallet, Calendar,
  AlertTriangle, CheckCircle, Settings, GripVertical, Eye, EyeOff,
  BarChart2, Receipt, ChevronRight, Activity, X, MapPin, Package,
  ListChecks, Bell, Download, FileSpreadsheet, FileText,
  ArrowUpRight, ArrowDownRight, Clock, Zap,
} from 'lucide-react'
import api from '@/lib/axios'
import { useAuthStore } from '../../stores/auth.store'
import { DashboardSkeleton } from '@/components/ui/Skeleton'

/* ── Types ─────────────────────────────────────────────────── */
interface DashboardWidget {
  id: string; type: string; title: string; module: string
  visible: boolean; order: number; size: 'sm' | 'md' | 'lg'
}
interface DashboardData {
  generatedAt: string
  user: { id: string; firstName: string; roles: string[] }
  kpis: {
    hr?: { activeEmployees: number; newHiresThisMonth: number; onLeave: number; grossPayroll: number; netPayroll: number }
    crm?: { activeOpportunities: number; wonCountThisMonth: number; wonValueThisMonth: number; totalPipelineValue: number }
    projects?: { activeProjects: number; myOpenTasks: number; overdueTasks: number }
    tickets?: { openTickets: number; urgentTickets: number; resolvedToday: number }
    treasury?: { totalBalance: number; accountCount: number }
    leaves?: { pendingCount: number }
  }
  lists: {
    myTasks?: Array<{ id: string; title: string; number: number; priority: string; dueDate?: string; project: { id: string; name: string; coverColor?: string }; status: { name: string; color: string } }>
    stagnantOpps?: Array<{ id: string; title: string; amount: number; scoreLevel: string; stage: { name: string; color: string }; assignedTo?: { firstName: string; lastName: string } | null }>
    overdueInvoices?: Array<{ id: string; number?: string; clientName: string; totalTtc: number; dueDate?: string; status: string }>
  }
  charts: {
    crmMonthlyTrend?: Array<{ month: string; won: number; pipeline: number; count: number }>
    hrHeadcountTrend?: Array<{ month: string; active: number; onLeave: number; newHires: number }>
    ticketsTrend?: Array<{ month: string; opened: number; resolved: number }>
  }
  recentActivity?: Array<{ id: string; action: string; resourceType: string; resourceId: string; createdAt: string; metadata?: any; user?: { firstName: string; lastName: string; avatarUrl?: string } }>
}

/* ── Helpers ────────────────────────────────────────────────── */
const fmtK = (v: number) =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M`
  : v >= 1_000 ? `${(v / 1_000).toFixed(0)}K`
  : String(v)
const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'
const fmtAgo = (d: string) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}min`
  if (s < 86400) return `${Math.floor(s / 3600)}h`
  return `${Math.floor(s / 86400)}j`
}
const PRIO: Record<string, string> = { LOW: 'var(--tx3)', MEDIUM: 'var(--brand-orange)', HIGH: '#F59E0B', URGENT: '#EF4444' }

/* ── Role check helpers ─────────────────────────────────────── */
const hasRole = (roles: string[], ...r: string[]) => r.some(x => roles.includes(x))
const isDirection = (roles: string[]) => hasRole(roles, 'SUPER_ADMIN', 'ADMIN', 'DG')
const isHR = (roles: string[]) => hasRole(roles, 'DRH', 'RH')
const isFinance = (roles: string[]) => hasRole(roles, 'DAF')
const isCommercial = (roles: string[]) => hasRole(roles, 'COMMERCIAL')
const isManager = (roles: string[]) => hasRole(roles, 'MANAGER')

/* ── Spinner ────────────────────────────────────────────────── */
function Spin() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div style={{ width: 30, height: 30, borderRadius: '50%', border: '3px solid var(--bd)', borderTopColor: 'var(--brand-orange)', animation: 'spin .7s linear infinite' }} />
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   KPI CARD — Modern 2026
═══════════════════════════════════════════════════════════ */
const KC: Record<string, { icon: string; bg: string; glow: string }> = {
  blue:   { icon: 'var(--brand-orange)', bg: '#FFF3E0', glow: 'rgba(245,124,0,0.08)' },
  violet: { icon: '#7C3AED',            bg: '#F3E8FF', glow: 'rgba(124,58,237,0.08)' },
  green:  { icon: '#10B981',            bg: '#ECFDF5', glow: 'rgba(16,185,129,0.08)' },
  amber:  { icon: '#F59E0B',            bg: '#FFFBEB', glow: 'rgba(245,158,11,0.08)' },
  red:    { icon: '#EF4444',            bg: '#FEF2F2', glow: 'rgba(239,68,68,0.08)' },
  indigo: { icon: '#6366F1',            bg: '#EEF2FF', glow: 'rgba(99,102,241,0.08)' },
  cyan:   { icon: '#06B6D4',            bg: '#ECFEFF', glow: 'rgba(6,182,212,0.08)' },
  rose:   { icon: '#F43F5E',            bg: '#FFF1F2', glow: 'rgba(244,63,94,0.08)' },
}

function KpiCard({ label, value, sub, icon: Icon, color = 'blue', link, trend }: {
  label: string; value: string | number; sub?: string; icon: React.ElementType
  color?: string; link?: string; trend?: string
}) {
  const navigate = useNavigate()
  const c = KC[color] ?? KC.blue
  const isWarning = trend && trend.includes('⚠')

  return (
    <div
      onClick={() => link && navigate(link)}
      className="nx-fade-up"
      style={{
        background: '#fff',
        border: '1px solid var(--bd)',
        borderRadius: 16,
        padding: '20px 22px',
        position: 'relative',
        overflow: 'hidden',
        cursor: link ? 'pointer' : undefined,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
      }}
      onMouseEnter={e => {
        if (link) {
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.boxShadow = `0 8px 28px ${c.glow}, 0 4px 12px rgba(0,0,0,0.04)`
          e.currentTarget.style.borderColor = `${c.icon}30`
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none'
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)'
        e.currentTarget.style.borderColor = 'var(--bd)'
      }}
    >
      {/* Subtle accent line top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${c.icon}, ${c.icon}80)`,
        borderRadius: '16px 16px 0 0',
        opacity: 0.8,
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={18} style={{ color: c.icon }} />
        </div>
        {link && (
          <div style={{
            width: 24, height: 24, borderRadius: 8,
            background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--tx4)', transition: 'all 0.15s',
          }}>
            <ChevronRight size={12} />
          </div>
        )}
      </div>

      <p style={{
        fontSize: 12, fontWeight: 600, color: 'var(--tx3)',
        letterSpacing: '0.02em', margin: '0 0 6px',
        fontFamily: '"Raleway", sans-serif',
      }}>{label}</p>

      <p style={{
        fontSize: 26, fontWeight: 800, color: 'var(--tx)',
        letterSpacing: '-0.03em', lineHeight: 1, margin: 0,
        fontFamily: '"Raleway", sans-serif',
      }}>{value}</p>

      {sub && (
        <p style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 8, lineHeight: 1.3 }}>{sub}</p>
      )}
      {trend && (
        <p style={{
          fontSize: 11.5, fontWeight: 600, marginTop: 8, display: 'flex', alignItems: 'center', gap: 4,
          color: isWarning ? '#EF4444' : '#F59E0B',
        }}>
          {isWarning ? <AlertTriangle size={12} /> : <Zap size={12} />}
          {trend}
        </p>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════
   SECTION HEADER — Clean 2026
═══════════════════════════════════════════════════════════ */
function SHeader({ title, icon: Icon, link }: { title: string; icon: React.ElementType; link?: string }) {
  const navigate = useNavigate()
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10,
          background: 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(245,124,0,0.20)',
        }}>
          <Icon size={14} style={{ color: '#fff' }} />
        </div>
        <h2 style={{
          fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 15,
          color: 'var(--tx)', margin: 0, letterSpacing: '-0.01em',
        }}>{title}</h2>
      </div>
      {link && (
        <button
          onClick={() => navigate(link)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, border: 'none',
            background: 'transparent', cursor: 'pointer', fontSize: 12,
            fontWeight: 600, color: 'var(--brand-orange)', fontFamily: '"Raleway", sans-serif',
            padding: '4px 8px', borderRadius: 6, transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-bg)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          Voir tout <ChevronRight size={13} />
        </button>
      )}
    </div>
  )
}

/* ── Row ─────────────────────────────────────────────────────── */
function Row({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
        borderRadius: 10, cursor: onClick ? 'pointer' : undefined,
        transition: 'background 0.15s',
        marginBottom: 2,
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = 'var(--surface-2)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      {children}
    </div>
  )
}

/* ── Empty state ──────────────────────────────────────────── */
function Empty({ msg }: { msg: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 16px' }}>
      <p style={{ fontSize: 13, color: 'var(--tx3)' }}>{msg}</p>
    </div>
  )
}

/* ── Chart Card ──────────────────────────────────────────── */
function ChartCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--bd)', borderRadius: 16,
      padding: '22px 24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
    }}>
      <SHeader title={title} icon={Icon} />
      <div style={{ width: '100%', height: 260 }}>
        {children}
      </div>
    </div>
  )
}

/* ── Recharts custom tooltip ────────────────────────────────── */
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{
      background: '#160C26', borderRadius: 10, padding: '12px 16px',
      boxShadow: '0 8px 24px rgba(22,12,38,0.30)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 8, fontWeight: 600 }}>{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color, fontSize: 13, margin: '3px 0', fontWeight: 700 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('fr-FR') : p.value}
        </p>
      ))}
    </div>
  )
}

/* ── Widget Config Modal ────────────────────────────────────── */
function WidgetMgr({ widgets, onSave, onClose }: { widgets: DashboardWidget[]; onSave: (ws: DashboardWidget[]) => void; onClose: () => void }) {
  const [local, setLocal] = useState<DashboardWidget[]>(() => [...widgets].sort((a, b) => a.order - b.order))
  const toggle = (id: string) => setLocal(p => p.map(w => (w.id === id ? { ...w, visible: !w.visible } : w)))
  const move = (idx: number, dir: -1 | 1) => {
    const arr = [...local]
    const target = idx + dir
    if (target < 0 || target >= arr.length) return
    ;[arr[idx], arr[target]] = [arr[target], arr[idx]]
    setLocal(arr.map((w, i) => ({ ...w, order: i + 1 })))
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(22,12,38,0.50)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: 16 }} onClick={onClose}>
      <div className="nx-scale-in" style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(22,12,38,0.18)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--bd)' }}>
          <h3 style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 16, margin: 0, color: 'var(--tx)' }}>Configurer les widgets</h3>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--bd)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--tx3)' }}><X size={14} /></button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 20px' }}>
          {local.map((w, idx) => (
            <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid var(--surface-2)' }}>
              <GripVertical size={14} color="var(--tx4)" style={{ cursor: 'grab', flexShrink: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                <button onClick={() => move(idx, -1)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 10, color: 'var(--tx3)' }}>▲</button>
                <button onClick={() => move(idx, 1)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 10, color: 'var(--tx3)' }}>▼</button>
              </div>
              <p style={{ flex: 1, fontSize: 13, color: w.visible ? 'var(--tx)' : 'var(--tx4)', margin: 0, fontWeight: 500 }}>{w.title}</p>
              <button onClick={() => toggle(w.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex' }}>
                {w.visible ? <Eye size={16} color="var(--brand-orange)" /> : <EyeOff size={16} color="var(--tx4)" />}
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 24px', borderTop: '1px solid var(--bd)', background: 'var(--surface-2)', borderRadius: '0 0 18px 18px' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 10, border: '1px solid var(--bd)', background: '#fff', cursor: 'pointer', fontSize: 13, color: 'var(--tx2)', fontFamily: '"Raleway", sans-serif', fontWeight: 600 }}>Annuler</button>
          <button onClick={() => { onSave(local); onClose() }} style={{ padding: '8px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))', cursor: 'pointer', fontSize: 13, color: '#fff', fontFamily: '"Raleway", sans-serif', fontWeight: 700, boxShadow: '0 2px 8px rgba(245,124,0,0.25)' }}>Sauvegarder</button>
        </div>
      </div>
    </div>
  )
}

/* ── LIST CARD wrapper ──────────────────────────────────────── */
function ListCard({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--bd)', borderRadius: 16,
      padding: '22px 20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
    }}>
      {children}
    </div>
  )
}

/* ── Export button ────────────────────────────────────────────── */
function ExportBtn({ label, icon: Icon, onClick, loading }: { label: string; icon: React.ElementType; onClick: () => void; loading?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
        border: '1px solid var(--bd)', background: '#fff', cursor: loading ? 'wait' : 'pointer',
        fontSize: 12.5, color: 'var(--tx2)', fontFamily: '"Raleway", sans-serif', fontWeight: 600,
        transition: 'all 0.15s', opacity: loading ? 0.6 : 1,
      }}
      onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = 'var(--brand-orange)'; e.currentTarget.style.background = 'var(--blue-bg)' } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = '#fff' }}
    >
      <Icon size={14} />
      {loading ? 'Export…' : label}
    </button>
  )
}

/* ══════════════════════════════════════════════════════════════
   PAGE PRINCIPALE
══════════════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showCfg, setShowCfg] = useState(false)
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null)

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard-data'],
    queryFn: () => api.get('/dashboard/data').then(r => r.data.data ?? r.data),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: (fc, err: any) => { if (err?.response?.status === 429) return false; return fc < 2 },
  })
  const { data: widgetsRaw = [] } = useQuery<any>({
    queryKey: ['dashboard-widgets'],
    queryFn: () => api.get('/dashboard/widgets').then(r => r.data.data ?? r.data),
    retry: (fc, err: any) => { if (err?.response?.status === 429) return false; return fc < 2 },
  })
  const widgets: DashboardWidget[] = Array.isArray(widgetsRaw) ? widgetsRaw : (widgetsRaw?.data ?? [])
  const saveWidgets = useMutation({
    mutationFn: (ws: DashboardWidget[]) => api.put('/dashboard/widgets', ws),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard-widgets'] }),
  })
  const vis = (id: string) => { const w = widgets.find(w => w.id === id); return w === undefined ? true : w.visible }

  /* ── Export handlers ── */
  const handleExport = async (type: 'excel' | 'pdf') => {
    setExporting(type)
    try {
      const endpoint = type === 'excel' ? '/dashboard/export/excel' : '/dashboard/export/pdf'
      const res = await api.get(endpoint, { responseType: 'blob' })
      const ext = type === 'excel' ? 'xlsx' : 'pdf'
      const filename = `dashboard_nexuscorp_${new Date().toISOString().slice(0, 10)}.${ext}`
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url; a.download = filename; document.body.appendChild(a); a.click()
      window.URL.revokeObjectURL(url); document.body.removeChild(a)
    } catch (err) { console.error('Export failed:', err) }
    finally { setExporting(null) }
  }

  if (isLoading || !data) return <DashboardSkeleton />

  const { kpis = {} as DashboardData['kpis'], lists = {} as DashboardData['lists'], charts = {} as DashboardData['charts'], recentActivity = [] } = data ?? {}
  const roles = data?.user?.roles ?? user?.roles ?? []
  const h = new Date().getHours()
  const greeting = h < 12 ? 'Bonjour' : h < 18 ? 'Bon après-midi' : 'Bonsoir'

  /* ── Role visibility ── */
  const showHR = isDirection(roles) || isHR(roles) || isFinance(roles)
  const showCRM = isDirection(roles) || isCommercial(roles) || isFinance(roles)
  const showProjects = isDirection(roles) || isManager(roles) || hasRole(roles, 'EMPLOYEE')
  const showTickets = isDirection(roles) || isManager(roles)
  const showTreasury = isDirection(roles) || isFinance(roles)
  const showLeaves = isDirection(roles) || isHR(roles)

  return (
    <div className="nx-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* ════════════════════ HEADER ════════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{
            fontFamily: '"Raleway", sans-serif', fontWeight: 800, fontSize: 26,
            color: 'var(--tx)', letterSpacing: '-0.035em', margin: '0 0 4px', lineHeight: 1.15,
          }}>
            {greeting}, {user?.firstName}
            <span style={{ color: 'var(--brand-orange)', marginLeft: 2 }}>.</span>
          </h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx3)', margin: 0, fontWeight: 400 }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <ExportBtn label="Excel" icon={FileSpreadsheet} onClick={() => handleExport('excel')} loading={exporting === 'excel'} />
          <ExportBtn label="PDF" icon={FileText} onClick={() => handleExport('pdf')} loading={exporting === 'pdf'} />
          <button
            onClick={() => setShowCfg(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10,
              border: '1px solid var(--bd)', background: '#fff', cursor: 'pointer', fontSize: 13,
              color: 'var(--tx2)', fontFamily: '"Raleway", sans-serif', fontWeight: 600,
              transition: 'all 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-orange)'; e.currentTarget.style.background = 'var(--blue-bg)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = '#fff' }}
          >
            <Settings size={14} /> Widgets
          </button>
        </div>
      </div>

      {/* ════════════════════ VARIANT TABS ════════════════════ */}
      <DashboardVariantTabs roles={roles} />

      {/* ════════════════════ KPI GRID 1 ════════════════════ */}
      <div className="nx-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {showHR && vis('hr_stats') && (
          <KpiCard label="Effectifs actifs" value={kpis.hr?.activeEmployees ?? 0} color="violet"
            sub={(kpis.hr?.newHiresThisMonth ?? 0) > 0 ? `+${kpis.hr?.newHiresThisMonth} ce mois` : 'Stable ce mois'}
            icon={Users} link="/hr/employees" />
        )}
        {showHR && vis('payroll_month') && (
          <KpiCard label="Masse salariale brute" value={fmtK(kpis.hr?.grossPayroll ?? 0) + ' XOF'} color="indigo"
            sub={`Net : ${fmtK(kpis.hr?.netPayroll ?? 0)} XOF`} icon={Wallet} link="/hr/payroll" />
        )}
        {showCRM && vis('crm_pipeline') && (
          <KpiCard label="Pipeline actif" value={kpis.crm?.activeOpportunities ?? 0} color="amber"
            sub={fmtK(kpis.crm?.totalPipelineValue ?? 0) + ' XOF en cours'} icon={TrendingUp} link="/crm" />
        )}
        {showCRM && vis('crm_won_month') && (
          <KpiCard label="CA gagné ce mois" value={fmtK(kpis.crm?.wonValueThisMonth ?? 0) + ' XOF'} color="green"
            sub={`${kpis.crm?.wonCountThisMonth ?? 0} deal${(kpis.crm?.wonCountThisMonth ?? 0) !== 1 ? 's' : ''} clôturé${(kpis.crm?.wonCountThisMonth ?? 0) !== 1 ? 's' : ''}`}
            icon={BarChart2} link="/crm/analytics" />
        )}
      </div>

      {/* ════════════════════ KPI GRID 2 ════════════════════ */}
      <div className="nx-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {showProjects && vis('projects_active') && (
          <KpiCard label="Projets actifs" value={kpis.projects?.activeProjects ?? 0} color="blue"
            sub={(kpis.projects?.myOpenTasks ?? 0) + ' tâches ouvertes'} icon={FolderKanban} link="/projects"
            trend={(kpis.projects?.overdueTasks ?? 0) > 0 ? `⚠ ${kpis.projects?.overdueTasks} en retard` : undefined} />
        )}
        {showTickets && vis('tickets_open') && (
          <KpiCard label="Tickets ouverts" value={kpis.tickets?.openTickets ?? 0} color="rose"
            sub={(kpis.tickets?.urgentTickets ?? 0) > 0 ? `${kpis.tickets?.urgentTickets} urgents` : 'Aucun urgent'}
            icon={Ticket} link="/tickets" />
        )}
        {showLeaves && vis('leaves_pending') && (
          <KpiCard label="Congés en attente" value={kpis.leaves?.pendingCount ?? 0} color="cyan"
            sub="Demandes à valider" icon={Calendar} link="/hr/leaves" />
        )}
        {showTreasury && vis('treasury_balance') && (
          <KpiCard label="Solde trésorerie" value={fmtK(kpis.treasury?.totalBalance ?? 0) + ' XOF'} color="blue"
            sub={`${kpis.treasury?.accountCount ?? 0} compte${(kpis.treasury?.accountCount ?? 0) !== 1 ? 's' : ''}`}
            icon={Wallet} link="/treasury/accounts" />
        )}
      </div>

      {/* ════════════════════ CHARTS ════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 18 }}>
        {showCRM && vis('chart_crm_monthly') && charts.crmMonthlyTrend && charts.crmMonthlyTrend.length > 0 && (
          <ChartCard title="CA mensuel CRM" icon={TrendingUp}>
            <ResponsiveContainer>
              <AreaChart data={charts.crmMonthlyTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradWon" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradPipeline" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--brand-orange)" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="var(--brand-orange)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-3)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--tx4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--tx4)' }} axisLine={false} tickLine={false} tickFormatter={v => fmtK(v)} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, fontFamily: '"Raleway", sans-serif', paddingTop: 8 }} />
                <Area type="monotone" dataKey="won" name="CA gagné" stroke="#10B981" fill="url(#gradWon)" strokeWidth={2.5} />
                <Area type="monotone" dataKey="pipeline" name="Pipeline" stroke="var(--brand-orange)" fill="url(#gradPipeline)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {showHR && vis('chart_hr_headcount') && charts.hrHeadcountTrend && charts.hrHeadcountTrend.length > 0 && (
          <ChartCard title="Évolution effectifs" icon={Users}>
            <ResponsiveContainer>
              <BarChart data={charts.hrHeadcountTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-3)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--tx4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--tx4)' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, fontFamily: '"Raleway", sans-serif', paddingTop: 8 }} />
                <Bar dataKey="active" name="Actifs" fill="var(--brand-amber)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="newHires" name="Embauches" fill="var(--brand-orange)" radius={[6, 6, 0, 0]} />
                <Bar dataKey="onLeave" name="En congé" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {showTickets && vis('chart_tickets_trend') && charts.ticketsTrend && charts.ticketsTrend.length > 0 && (
          <ChartCard title="Tendance tickets" icon={Ticket}>
            <ResponsiveContainer>
              <BarChart data={charts.ticketsTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-3)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--tx4)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--tx4)' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, fontFamily: '"Raleway", sans-serif', paddingTop: 8 }} />
                <Bar dataKey="opened" name="Ouverts" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                <Bar dataKey="resolved" name="Résolus" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* ════════════════════ LISTS ════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 18 }}>
        {showProjects && vis('my_tasks') && (
          <ListCard>
            <SHeader title="Mes tâches" icon={CheckCircle} link="/projects" />
            {!lists.myTasks || (lists.myTasks ?? []).length === 0 ? (
              <Empty msg="Aucune tâche ouverte 🎉" />
            ) : (
              (lists.myTasks ?? []).slice(0, 7).map(t => (
                <Row key={t.id} onClick={() => navigate(`/projects/${t.project.id}/board`)}>
                  <div style={{ width: 4, height: 32, borderRadius: 99, flexShrink: 0, background: PRIO[t.priority] }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: 13.5, color: 'var(--tx)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{t.title}</p>
                    <p style={{ fontSize: 12, color: 'var(--tx3)', margin: '2px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {t.project.name}{t.dueDate && <><Clock size={10} /> {fmtDate(t.dueDate)}</>}
                    </p>
                  </div>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                    background: `${t.status.color}15`, color: t.status.color,
                    flexShrink: 0, fontFamily: '"Raleway", sans-serif',
                  }}>{t.status.name}</span>
                </Row>
              ))
            )}
          </ListCard>
        )}

        {showCRM && vis('crm_stagnant') && (
          <ListCard>
            <SHeader title="Opps. à relancer" icon={AlertTriangle} link="/crm" />
            {!lists?.stagnantOpps || (lists.stagnantOpps ?? []).length === 0 ? (
              <Empty msg="Aucune opportunité stagnante" />
            ) : (
              (lists.stagnantOpps ?? []).map(opp => (
                <Row key={opp.id} onClick={() => navigate('/crm')}>
                  <div style={{ width: 4, height: 36, borderRadius: 99, flexShrink: 0, background: opp.stage.color || 'var(--tx3)' }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: 13.5, color: 'var(--tx)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{opp.title}</p>
                    <p style={{ fontSize: 12, color: 'var(--tx3)', margin: '2px 0 0' }}>{opp.stage.name}{opp.assignedTo && ` · ${opp.assignedTo.firstName}`}</p>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand-orange)', flexShrink: 0, fontFamily: '"Raleway", sans-serif' }}>{fmtK(opp.amount)}</span>
                </Row>
              ))
            )}
          </ListCard>
        )}

        {(isFinance(roles) || isDirection(roles)) && vis('invoices_overdue') && (
          <ListCard>
            <SHeader title="Factures en retard" icon={Receipt} link="/invoices" />
            {!lists.overdueInvoices || (lists.overdueInvoices ?? []).length === 0 ? (
              <Empty msg="Aucune facture en retard" />
            ) : (
              (lists.overdueInvoices ?? []).map(inv => (
                <Row key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: '#FEF2F2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Receipt size={15} style={{ color: '#EF4444' }} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: 13.5, color: 'var(--tx)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}>{inv.number ?? inv.id.slice(-8)}</p>
                    <p style={{ fontSize: 12, color: 'var(--tx3)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.clientName}</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 800, color: '#EF4444', margin: 0, fontFamily: '"Raleway", sans-serif' }}>{fmtK(inv.totalTtc)}</p>
                    <p style={{ fontSize: 11, color: 'var(--tx4)', margin: '2px 0 0' }}>{fmtDate(inv.dueDate)}</p>
                  </div>
                </Row>
              ))
            )}
          </ListCard>
        )}
      </div>

      {/* ════════════════════ ACTIVITY FEED ════════════════════ */}
      {vis('recent_activity') && (
        <div style={{
          background: '#fff', border: '1px solid var(--bd)', borderRadius: 16,
          padding: '22px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <SHeader title="Activité récente" icon={Activity} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0 28px' }}>
            {(recentActivity ?? []).slice(0, 12).map(log => (
              <div key={log.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px',
                borderRadius: 8, transition: 'background 0.1s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))',
                  flexShrink: 0, boxShadow: '0 0 6px rgba(245,124,0,0.20)',
                }} />
                <p style={{ fontSize: 13, color: 'var(--tx2)', flex: 1, margin: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', lineHeight: 1.4 }}>
                  <span style={{ fontWeight: 700, color: 'var(--tx)' }}>
                    {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Système'}
                  </span>{' '}
                  {log.action.toLowerCase().replace(/_/g, ' ')}{' '}
                  <span style={{ color: 'var(--tx4)' }}>{log.resourceType}</span>
                </p>
                <span style={{ fontSize: 11, color: 'var(--tx4)', flexShrink: 0, fontWeight: 500 }}>{fmtAgo(log.createdAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showCfg && <WidgetMgr widgets={widgets} onSave={ws => saveWidgets.mutate(ws)} onClose={() => setShowCfg(false)} />}
    </div>
  )
}

// ━━━ Sprint 2 — Dashboard Variant Tabs ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function DashboardVariantTabs({ roles }: { roles: string[] }) {
  const [variant, setVariant] = useState<'direction' | 'chef_projet' | null>(null)
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-variant', variant],
    queryFn: () => {
      const endpoint = variant === 'direction' ? '/dashboard/direction'
        : '/dashboard/chef-projet'
      return api.get(endpoint).then(r => r.data?.data ?? r.data)
    },
    enabled: !!variant,
  })

  const tabs = [
    ...(isDirection(roles) ? [{ key: 'direction' as const, label: '📊 Direction' }] : []),
    ...(hasRole(roles, 'MANAGER', 'CHEF_PROJET', 'DG', 'ADMIN', 'SUPER_ADMIN') ? [{ key: 'chef_projet' as const, label: '📋 Chef de Projet' }] : []),
  ]

  if (tabs.length === 0) return null

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => setVariant(v => v === t.key ? null : t.key)}
            style={{
              padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              fontFamily: '"Raleway", sans-serif', cursor: 'pointer', transition: 'all 0.2s',
              border: variant === t.key ? 'none' : '1px solid var(--bd)',
              background: variant === t.key ? 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))' : '#fff',
              color: variant === t.key ? '#fff' : 'var(--tx2)',
              boxShadow: variant === t.key ? '0 2px 8px rgba(245,124,0,0.25)' : 'none',
            }}
          >
            {t.label}
          </button>
        ))}
        {variant && (
          <button onClick={() => setVariant(null)} style={{
            padding: '8px 12px', borderRadius: 10, border: 'none', background: 'transparent',
            cursor: 'pointer', fontSize: 12, color: 'var(--tx3)', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <X size={14} /> Fermer
          </button>
        )}
      </div>

      {variant && (
        <div className="nx-fade-up" style={{
          marginTop: 16, background: '#fff', border: '1px solid var(--bd)', borderRadius: 16,
          padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          {isLoading ? (
            <Spin />
          ) : variant === 'direction' ? (
            <DirectionVariant data={data} />
          ) : (
            <ChefProjetVariant data={data} navigate={navigate} />
          )}
        </div>
      )}
    </div>
  )
}

function DirectionVariant({ data }: { data: any }) {
  if (!data) return null
  const kpis = data.kpis ?? {}
  const toNum = (d: any) => Number(d ?? 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <h3 style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--tx)', margin: 0 }}>Vue Direction</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
        {[
          { label: 'Projets actifs', value: kpis.projects?.active ?? 0, bg: '#FFF8E1', color: 'var(--brand-orange)' },
          { label: 'Effectifs', value: kpis.hr?.activeEmployees ?? 0, bg: '#E8F5E9', color: '#2E7D32' },
          { label: 'Opportunités', value: kpis.crm?.totalOpportunities ?? 0, bg: '#E3F2FD', color: '#1565C0' },
          { label: 'Factures en retard', value: data.lists?.overdueInvoices?.length ?? 0, bg: '#FCE4EC', color: '#C62828' },
        ].map((k, i) => (
          <div key={i} style={{ background: k.bg, borderRadius: 14, padding: '18px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: k.color, fontFamily: '"Raleway", sans-serif' }}>{k.value}</div>
            <div style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {data.budgets?.length > 0 && (
        <div>
          <h4 style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--tx)', margin: '0 0 10px' }}>Budgets actifs</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.budgets.slice(0, 5).map((b: any) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px' }}>
                <span style={{ fontWeight: 600 }}>{b.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 80, height: 6, background: 'var(--surface-3)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--brand-orange)', borderRadius: 99, width: `${Math.min(100, (toNum(b.spentAmount) / toNum(b.totalAmount || 1)) * 100)}%` }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--tx3)', fontWeight: 600, minWidth: 36, textAlign: 'right' }}>{Math.round((toNum(b.spentAmount) / toNum(b.totalAmount || 1)) * 100)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ChefProjetVariant({ data, navigate }: { data: any; navigate: any }) {
  if (!data) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <h3 style={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--tx)', margin: 0 }}>Mes Projets</h3>
      {data.myProjects?.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {data.myProjects.map((p: any) => (
            <div key={p.id} style={{
              border: '1px solid var(--bd)', borderRadius: 14, padding: '18px 16px',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
              onClick={() => navigate(`/projects/${p.id}/board`)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-orange)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(245,124,0,0.08)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.boxShadow = 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--tx)' }}>{p.name}</span>
                <span style={{
                  fontSize: 10.5, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                  background: p.status === 'IN_PROGRESS' ? '#E3F2FD' : p.status === 'PLANNING' ? '#FFF8E1' : 'var(--surface-2)',
                  color: p.status === 'IN_PROGRESS' ? '#1565C0' : p.status === 'PLANNING' ? '#B45309' : 'var(--tx3)',
                }}>{p.status}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--tx3)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>{p._count?.tasks ?? 0} tâches</span>
                <span>{p._count?.members ?? 0} membres</span>
                {p.budgetTotal && <span>{Math.round((Number(p.budgetSpent ?? 0) / Number(p.budgetTotal)) * 100)}% budget</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Empty msg="Aucun projet en cours" />
      )}
    </div>
  )
}

